import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Separator } from "@/shared/components/ui/separator";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/shared/components/ui/dropdown-menu";
import { Phone, Clock, Target, TrendingUp, TrendingDown, ChevronDown } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from "recharts";
import { NavLink, useNavigate } from "react-router-dom";
import { useSubscriptionGuard } from "@/shared/hooks/useSubscriptionGuard";
import { SubscriptionRequiredScreen } from "@/features/billing/components/SubscriptionRequiredScreen";

const timeFrameOptions = [
  { label: "7 Days", value: "7days", active: true },
  { label: "30 Days", value: "30days", active: false },
  { label: "3 Months", value: "3months", active: false },
  { label: "6 Months", value: "6months", active: false },
  { label: "1 Year", value: "1year", active: false },
  { label: "All Time", value: "alltime", active: false },
];

// Data for different time frames
const dataByTimeFrame = {
  "7days": {
    callTrend: [
      { month: "Mon", calls: 405 },
      { month: "Tue", calls: 412 },
      { month: "Wed", calls: 398 },
      { month: "Thu", calls: 425 },
      { month: "Fri", calls: 441 },
      { month: "Sat", calls: 378 },
      { month: "Sun", calls: 389 },
    ],
    callDuration: [
      { month: "Mon", duration: 10.2 },
      { month: "Tue", duration: 10.4 },
      { month: "Wed", duration: 9.8 },
      { month: "Thu", duration: 10.6 },
      { month: "Fri", duration: 10.5 },
      { month: "Sat", duration: 9.9 },
      { month: "Sun", duration: 10.1 },
    ],
    dealClosed: [
      { month: "Mon", deals: 52 },
      { month: "Tue", deals: 58 },
      { month: "Wed", deals: 49 },
      { month: "Thu", deals: 65 },
      { month: "Fri", deals: 71 },
      { month: "Sat", deals: 43 },
      { month: "Sun", deals: 46 },
    ],
    bidSent: [
      { month: "Mon", deals: 67 },
      { month: "Tue", deals: 72 },
      { month: "Wed", deals: 68 },
      { month: "Thu", deals: 78 },
      { month: "Fri", deals: 82 },
      { month: "Sat", deals: 59 },
      { month: "Sun", deals: 62 },
    ],
    notInterested: [
      { month: "Mon", deals: 23 },
      { month: "Tue", deals: 26 },
      { month: "Wed", deals: 21 },
      { month: "Thu", deals: 29 },
      { month: "Fri", deals: 31 },
      { month: "Sat", deals: 18 },
      { month: "Sun", deals: 20 },
    ],
    totals: { calls: 2847, duration: 10.5, deals: 384 }
  },
  "30days": {
    callTrend: [
      { month: "Week 1", calls: 1820 },
      { month: "Week 2", calls: 1965 },
      { month: "Week 3", calls: 2134 },
      { month: "Week 4", calls: 2298 },
    ],
    callDuration: [
      { month: "Week 1", duration: 9.2 },
      { month: "Week 2", duration: 9.7 },
      { month: "Week 3", duration: 10.1 },
      { month: "Week 4", duration: 10.5 },
    ],
    dealClosed: [
      { month: "Week 1", deals: 245 },
      { month: "Week 2", deals: 289 },
      { month: "Week 3", deals: 325 },
      { month: "Week 4", deals: 384 },
    ],
    bidSent: [
      { month: "Week 1", deals: 312 },
      { month: "Week 2", deals: 356 },
      { month: "Week 3", deals: 398 },
      { month: "Week 4", deals: 445 },
    ],
    notInterested: [
      { month: "Week 1", deals: 134 },
      { month: "Week 2", deals: 152 },
      { month: "Week 3", deals: 165 },
      { month: "Week 4", deals: 178 },
    ],
    totals: { calls: 8217, duration: 10.5, deals: 1243 }
  },
  "3months": {
    callTrend: [
      { month: "Month 1", calls: 6245 },
      { month: "Month 2", calls: 7189 },
      { month: "Month 3", calls: 8217 },
    ],
    callDuration: [
      { month: "Month 1", duration: 8.9 },
      { month: "Month 2", duration: 9.7 },
      { month: "Month 3", duration: 10.5 },
    ],
    dealClosed: [
      { month: "Month 1", deals: 892 },
      { month: "Month 2", deals: 1067 },
      { month: "Month 3", deals: 1243 },
    ],
    bidSent: [
      { month: "Month 1", deals: 1156 },
      { month: "Month 2", deals: 1298 },
      { month: "Month 3", deals: 1445 },
    ],
    notInterested: [
      { month: "Month 1", deals: 456 },
      { month: "Month 2", deals: 512 },
      { month: "Month 3", deals: 578 },
    ],
    totals: { calls: 21651, duration: 10.5, deals: 3202 }
  },
  "6months": {
    callTrend: [
      { month: "Jan", calls: 4521 },
      { month: "Feb", calls: 5234 },
      { month: "Mar", calls: 5967 },
      { month: "Apr", calls: 6712 },
      { month: "May", calls: 7456 },
      { month: "Jun", calls: 8217 },
    ],
    callDuration: [
      { month: "Jan", duration: 8.2 },
      { month: "Feb", duration: 8.7 },
      { month: "Mar", duration: 9.1 },
      { month: "Apr", duration: 9.6 },
      { month: "May", duration: 10.1 },
      { month: "Jun", duration: 10.5 },
    ],
    dealClosed: [
      { month: "Jan", deals: 678 },
      { month: "Feb", deals: 789 },
      { month: "Mar", deals: 891 },
      { month: "Apr", deals: 1034 },
      { month: "May", deals: 1156 },
      { month: "Jun", deals: 1243 },
    ],
    bidSent: [
      { month: "Jan", deals: 867 },
      { month: "Feb", deals: 978 },
      { month: "Mar", deals: 1089 },
      { month: "Apr", deals: 1234 },
      { month: "May", deals: 1345 },
      { month: "Jun", deals: 1445 },
    ],
    notInterested: [
      { month: "Jan", deals: 234 },
      { month: "Feb", deals: 289 },
      { month: "Mar", deals: 345 },
      { month: "Apr", deals: 412 },
      { month: "May", deals: 489 },
      { month: "Jun", deals: 578 },
    ],
    totals: { calls: 38107, duration: 10.5, deals: 5791 }
  },
  "1year": {
    callTrend: [
      { month: "Q1", calls: 15722 },
      { month: "Q2", calls: 19885 },
      { month: "Q3", calls: 24567 },
      { month: "Q4", calls: 38107 },
    ],
    callDuration: [
      { month: "Q1", duration: 7.8 },
      { month: "Q2", duration: 8.5 },
      { month: "Q3", duration: 9.2 },
      { month: "Q4", duration: 10.5 },
    ],
    dealClosed: [
      { month: "Q1", deals: 2134 },
      { month: "Q2", deals: 3245 },
      { month: "Q3", deals: 4567 },
      { month: "Q4", deals: 5791 },
    ],
    bidSent: [
      { month: "Q1", deals: 2934 },
      { month: "Q2", deals: 3756 },
      { month: "Q3", deals: 4689 },
      { month: "Q4", deals: 5823 },
    ],
    notInterested: [
      { month: "Q1", deals: 1234 },
      { month: "Q2", deals: 1567 },
      { month: "Q3", deals: 1789 },
      { month: "Q4", deals: 2345 },
    ],
    totals: { calls: 98281, duration: 10.5, deals: 15737 }
  },
  "alltime": {
    callTrend: [
      { month: "2021", calls: 45234 },
      { month: "2022", calls: 67891 },
      { month: "2023", calls: 84567 },
      { month: "2024", calls: 98281 },
    ],
    callDuration: [
      { month: "2021", duration: 6.8 },
      { month: "2022", duration: 7.9 },
      { month: "2023", duration: 9.1 },
      { month: "2024", duration: 10.5 },
    ],
    dealClosed: [
      { month: "2021", deals: 6789 },
      { month: "2022", deals: 9456 },
      { month: "2023", deals: 12678 },
      { month: "2024", deals: 15737 },
    ],
    bidSent: [
      { month: "2021", deals: 8934 },
      { month: "2022", deals: 11567 },
      { month: "2023", deals: 14789 },
      { month: "2024", deals: 17823 },
    ],
    notInterested: [
      { month: "2021", deals: 3456 },
      { month: "2022", deals: 4789 },
      { month: "2023", deals: 6123 },
      { month: "2024", deals: 7845 },
    ],
    totals: { calls: 295973, duration: 10.5, deals: 44660 }
  }
};

const sessionStatusData = [
  { name: "Deal Closed", value: 18, color: "#86efac" },
  { name: "Bid Sent", value: 25, color: "#a5b4fc" },
  { name: "Not Interested", value: 15, color: "#fda4af" },
  { name: "N/A", value: 8, color: "#a855f7" },
];

const ReportsPage = () => {
  const navigate = useNavigate();
  const { canAccess, reason } = useSubscriptionGuard();
  const [selectedTimeFrame, setSelectedTimeFrame] = useState("7days");
  const [selectedMetric, setSelectedMetric] = useState<"calls" | "duration" | "deals" | null>(null);
  const [selectedDealType, setSelectedDealType] = useState<"Deal Closed" | "Bid Sent" | "Not Interested">("Deal Closed");

  // Check subscription access (trial users can access)
  if (!canAccess) {
    return (
      <SubscriptionRequiredScreen
        feature="Reports"
        reason={reason}
        onUpgrade={() => navigate('/profile?tab=billing')}
      />
    );
  }

  const currentData = dataByTimeFrame[selectedTimeFrame as keyof typeof dataByTimeFrame];

  const getChartData = () => {
    switch (selectedMetric) {
      case "calls":
        return { data: currentData.callTrend, dataKey: "calls", title: "Total Calls Over Time", color: "#3b82f6" };
      case "duration":
        return { data: currentData.callDuration, dataKey: "duration", title: "Average Call Duration (Minutes)", color: "#10b981" };
      case "deals":
        const dealData = selectedDealType === "Deal Closed" ? currentData.dealClosed :
                        selectedDealType === "Bid Sent" ? currentData.bidSent : currentData.notInterested;
        const dealColor = selectedDealType === "Deal Closed" ? "#86efac" :
                         selectedDealType === "Bid Sent" ? "#a5b4fc" : "#fda4af";
        return { data: dealData, dataKey: "deals", title: `${selectedDealType} Over Time`, color: dealColor };
      default:
        return null;
    }
  };

  const chartConfig = getChartData();

  return (
    <div className="p-6 space-y-6 bg-background min-h-screen">
      {/* Breadcrumb */}
      <div className="flex items-center text-sm text-muted-foreground">
        <NavLink 
          to="/" 
          className="hover:text-primary transition-colors cursor-pointer"
        >
          Home
        </NavLink>
        <span className="mx-2">›</span>
        <button 
          onClick={() => window.location.reload()}
          className="text-primary hover:underline cursor-pointer"
        >
          Reports
        </button>
      </div>

      {/* Time Frame Selector */}
      <div className="flex flex-wrap gap-2">
        {timeFrameOptions.map((option) => (
          <Button
            key={option.value}
            variant={selectedTimeFrame === option.value ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedTimeFrame(option.value)}
            className="h-8"
          >
            {option.label}
          </Button>
        ))}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${selectedMetric === 'calls' ? 'ring-2 ring-primary' : ''}`}
          onClick={() => setSelectedMetric(selectedMetric === 'calls' ? null : 'calls')}
        >
          <CardHeader className="flex flex-row items-center justify-center space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Calls</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <div className="text-2xl font-bold">{currentData.totals.calls.toLocaleString()}</div>
            <div className="flex items-center justify-center text-xs text-emerald-600">
              <TrendingUp className="h-3 w-3 mr-1" />
              12.5% vs last period
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${selectedMetric === 'duration' ? 'ring-2 ring-primary' : ''}`}
          onClick={() => setSelectedMetric(selectedMetric === 'duration' ? null : 'duration')}
        >
          <CardHeader className="flex flex-row items-center justify-center space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Call duration</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <div className="text-2xl font-bold">{currentData.totals.duration} Minute</div>
            <div className="flex items-center justify-center text-xs text-red-600">
              <TrendingDown className="h-3 w-3 mr-1" />
              3.2% vs last period
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${selectedMetric === 'deals' ? 'ring-2 ring-primary' : ''}`}
          onClick={() => setSelectedMetric(selectedMetric === 'deals' ? null : 'deals')}
        >
          <CardHeader className="flex flex-row items-center justify-center space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Closed Deals</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <div className="text-2xl font-bold">{currentData.totals.deals.toLocaleString()}</div>
            <div className="flex items-center justify-center">
              <div className="flex items-center text-xs text-emerald-600">
                <TrendingUp className="h-3 w-3 mr-1" />
                8.7% vs last period
              </div>
            </div>
            <div className="flex justify-center mt-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-background border z-50">
                  <DropdownMenuItem 
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedDealType("Deal Closed");
                      setSelectedMetric("deals");
                    }}
                    className="cursor-pointer hover:bg-green-100 hover:text-green-800"
                  >
                    Deals Closed
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedDealType("Bid Sent");
                      setSelectedMetric("deals");
                    }}
                    className="cursor-pointer hover:bg-indigo-100 hover:text-indigo-800"
                  >
                    Bid Sent
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedDealType("Not Interested");
                      setSelectedMetric("deals");
                    }}
                    className="cursor-pointer hover:bg-rose-100 hover:text-rose-800"
                  >
                    Not Interested
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Dynamic Chart based on selected metric */}
        {chartConfig && (
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>{chartConfig.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={chartConfig.data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Line 
                    type="monotone" 
                    dataKey={chartConfig.dataKey} 
                    stroke={chartConfig.color} 
                    strokeWidth={2}
                    dot={{ fill: chartConfig.color, strokeWidth: 2, r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Default Charts when no metric is selected */}
        {!selectedMetric && (
          <>
            {/* Call Trend Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Call Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={currentData.callTrend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Line 
                      type="monotone" 
                      dataKey="calls" 
                      stroke="#3b82f6" 
                      strokeWidth={2}
                      dot={{ fill: "#3b82f6", strokeWidth: 2, r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Session Status Overview */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Session Status Overview</CardTitle>
                <div className="flex items-center gap-4 text-sm">
                  <span>Last 7 Days</span>
                  <Separator orientation="vertical" className="h-4" />
                  <span>All Team Members</span>
                </div>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={sessionStatusData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value">
                      {sessionStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default ReportsPage;